import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Td,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaPlus, FaUserPlus, FaClipboardList, FaUserTie } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import {
  listMissions,
  listSentConsultantProposals,
} from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_MISSION_STATUSES,
  PARTNERS_MISSION_STATUS_COLORS,
  PARTNERS_MISSION_STATUS_LABELS,
  PARTNERS_MISSION_STATUS_LABEL_KEYS,
  PROPOSAL_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import MissionFormModal from "./MissionFormModal";
import MissionDetailModal from "./MissionDetailModal";
import ConsultantProposalFormModal from "./ConsultantProposalFormModal";
import ConsultantProposalDetailModal from "./ConsultantProposalDetailModal";
import PartnersMonthlyChart from "../../../components/Portal/Partners/PartnersMonthlyChart";

// Shared sx for the compact filter-row selects (matches FilterSearch's 31px).
const FILTER_SELECT_SX = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  fontWeight: 600,
};

const PartnersMissions = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  // Active sub-tab (0 = missions, 1 = consultants) — drives the chart scope.
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Missions tab state
  const [missions, setMissions] = useState([]);
  const [missionsLoading, setMissionsLoading] = useState(false);
  const [missionsPage, setMissionsPage] = useState(1);
  const [missionsPageSize, setMissionsPageSize] = useState(20);
  const [missionsTotalPages, setMissionsTotalPages] = useState(1);
  const [missionsTotalItems, setMissionsTotalItems] = useState(0);
  const [missionsSearch, setMissionsSearch] = useState("");
  const [missionsStatusFilter, setMissionsStatusFilter] = useState("");
  const missionCreateModal = useDisclosure();
  const [selectedMissionId, setSelectedMissionId] = useState(null);
  const missionDetailModal = useDisclosure();

  // Consultants (proposals) tab state
  const [proposals, setProposals] = useState([]);
  const [proposalsLoading, setProposalsLoading] = useState(false);
  const [proposalsPage, setProposalsPage] = useState(1);
  const [proposalsPageSize, setProposalsPageSize] = useState(20);
  const [proposalsTotalPages, setProposalsTotalPages] = useState(1);
  const [proposalsTotalItems, setProposalsTotalItems] = useState(0);
  const [proposalsStatusFilter, setProposalsStatusFilter] = useState("");
  const proposalCreateModal = useDisclosure();
  const [selectedProposal, setSelectedProposal] = useState(null);
  const proposalDetailModal = useDisclosure();

  const fetchMissions = useCallback(
    async (silent = false) => {
      if (!silent) setMissionsLoading(true);
      try {
        const params = { page: missionsPage, pageSize: missionsPageSize };
        if (missionsSearch.trim()) params.search = missionsSearch.trim();
        if (missionsStatusFilter) params.status = missionsStatusFilter;

        const res = await listMissions(params, {
          buId: selectedBusinessUnitId || undefined,
        });
        setMissions(res.items || []);
        setMissionsTotalPages(res.totalPages || 1);
        setMissionsTotalItems(res.totalItems || 0);
      } catch {
        if (!silent) {
          toast({
            title: t(
              "partners.sentRequests.toasts.loadMissionsError",
              "Failed to load missions"
            ),
            status: "error",
          });
        }
      } finally {
        setMissionsLoading(false);
      }
    },
    [
      missionsPage,
      missionsPageSize,
      missionsSearch,
      missionsStatusFilter,
      selectedBusinessUnitId,
      t,
      toast,
    ]
  );

  const fetchProposals = useCallback(
    async (silent = false) => {
      if (!silent) setProposalsLoading(true);
      try {
        const params = { page: proposalsPage, pageSize: proposalsPageSize };
        if (proposalsStatusFilter) params.status = proposalsStatusFilter;
        const res = await listSentConsultantProposals(params, {
          buId: selectedBusinessUnitId || undefined,
        });
        setProposals(res.items || []);
        setProposalsTotalPages(res.totalPages || 1);
        setProposalsTotalItems(res.totalItems || 0);
      } catch {
        if (!silent) {
          toast({
            title: t(
              "partners.sentRequests.toasts.loadProposalsError",
              "Failed to load consultant proposals"
            ),
            status: "error",
          });
        }
      } finally {
        setProposalsLoading(false);
      }
    },
    [
      proposalsPage,
      proposalsPageSize,
      proposalsStatusFilter,
      selectedBusinessUnitId,
      t,
      toast,
    ]
  );

  useEffect(() => {
    if (!isBUContextLoaded) return;
    fetchMissions();
  }, [fetchMissions, isBUContextLoaded]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    fetchProposals();
  }, [fetchProposals, isBUContextLoaded]);

  useEffect(() => {
    if (!isBUContextLoaded) return;
    const id = setInterval(() => {
      fetchMissions(true);
      fetchProposals(true);
    }, 30000);
    return () => clearInterval(id);
  }, [fetchMissions, fetchProposals, isBUContextLoaded]);

  const handleMissionRowClick = (mission) => {
    setSelectedMissionId(mission.id);
    missionDetailModal.onOpen();
  };

  const handleMissionSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      setMissionsPage(1);
      fetchMissions();
    }
  };

  const purposeLabel = (purpose) => {
    const map = {
      contact_pool: t("partners.missions.purposeLabels.contactPool", "Contact Pool"),
      propose_assignment: t(
        "partners.missions.purposeLabels.proposeAssignment",
        "Assignment"
      ),
    };
    return map[purpose] || purpose || "-";
  };

  const proposalStatusLabel = (status) =>
    t(`partners.consultantProposals.statusLabels.${status}`, status || "-");

  const missionColumns = [
    { key: "title", label: t("partners.missions.table.title", "Title"), width: "26%" },
    { key: "purpose", label: t("partners.missions.table.purpose", "Purpose"), width: "15%" },
    { key: "status", label: t("partners.missions.table.status", "Status"), width: "14%" },
    { key: "recipients", label: t("partners.missions.table.recipients", "Recipients"), width: "13%", align: "right" },
    { key: "applications", label: t("partners.missions.table.applications", "Applications"), width: "13%", align: "right" },
    { key: "created", label: t("partners.missions.table.created", "Created"), width: "19%" },
  ];

  const proposalColumns = [
    { key: "recipient", label: t("partners.consultantProposals.table.recipient", "Recipient"), width: "22%" },
    { key: "role", label: t("partners.consultantProposals.table.role", "Role") },
    { key: "dailyRate", label: t("partners.consultantProposals.table.dailyRate", "Daily rate"), width: "16%", align: "right" },
    { key: "availability", label: t("partners.consultantProposals.table.availability", "Available"), width: "15%" },
    { key: "status", label: t("partners.consultantProposals.table.status", "Status"), width: "14%" },
    { key: "created", label: t("partners.consultantProposals.table.created", "Sent"), width: "14%" },
  ];

  return (
    // Bleed past PortalLayout's p={6} so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="AP"
        eyebrow={t("partners.module.eyebrow", "ASAP Partners")}
        title={t("partners.sentRequests.title", "Sent Requests")}
      />

      <Box p="20px">
        <Box mb="12px">
          <PartnersMonthlyChart
            scope={activeTabIndex === 1 ? "sent_consultants" : "sent_missions"}
            buId={selectedBusinessUnitId || undefined}
          />
        </Box>

        <Tabs
          variant="line"
          colorScheme="brand"
          index={activeTabIndex}
          onChange={(idx) => setActiveTabIndex(idx)}
        >
          <TabList mb="12px" borderColor="portal.line">
            <Tab fontWeight="800" fontSize="13px">
              {t("partners.sentRequests.tabs.missions", "Missions")}
            </Tab>
            <Tab fontWeight="800" fontSize="13px">
              {t("partners.sentRequests.tabs.consultants", "Consultants")}
            </Tab>
          </TabList>

          <TabPanels>
            {/* ─── Missions Tab ─── */}
            <TabPanel p={0}>
              <Panel
                title={t("partners.sentRequests.tabs.missions", "Missions")}
                count={t("partners.missions.summary.total", {
                  count: missionsTotalItems,
                  defaultValue: "{{count}} mission(s)",
                })}
                actions={
                  <Button
                    variant="primary"
                    size="sm"
                    h="28px"
                    px="10px"
                    fontSize="11px"
                    leftIcon={<FaPlus size="9px" />}
                    onClick={missionCreateModal.onOpen}
                  >
                    {t("partners.missions.createButton", "New Mission")}
                  </Button>
                }
                filters={
                  <FilterBar>
                    <FilterSearch
                      placeholder={t(
                        "partners.missions.searchPlaceholder",
                        "Search by title..."
                      )}
                      value={missionsSearch}
                      onChange={(e) => setMissionsSearch(e.target.value)}
                      onKeyDown={handleMissionSearchKeyDown}
                    />
                    <Select
                      value={missionsStatusFilter}
                      onChange={(e) => {
                        setMissionsStatusFilter(e.target.value);
                        setMissionsPage(1);
                      }}
                      w={{ base: "100%", sm: "180px" }}
                      flex="0 0 auto"
                      placeholder={t("partners.missions.allStatuses", "All statuses")}
                      sx={FILTER_SELECT_SX}
                    >
                      {PARTNERS_MISSION_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {getStatusLabel(
                            t,
                            PARTNERS_MISSION_STATUS_LABEL_KEYS,
                            PARTNERS_MISSION_STATUS_LABELS,
                            s.value
                          )}
                        </option>
                      ))}
                    </Select>
                  </FilterBar>
                }
                footer={
                  <TablePagination
                    page={missionsPage}
                    pageSize={missionsPageSize}
                    totalItems={missionsTotalItems}
                    totalPages={missionsTotalPages}
                    onPageChange={setMissionsPage}
                    onPageSizeChange={(sz) => {
                      setMissionsPageSize(sz);
                      setMissionsPage(1);
                    }}
                  />
                }
              >
                <DataTable
                  columns={missionColumns}
                  rows={missions}
                  loading={missionsLoading}
                  emptyState={
                    <EmptyState
                      icon={<FaClipboardList />}
                      title={t("partners.missions.empty", "No missions yet")}
                      hint={t(
                        "partners.missions.emptyHint",
                        "Create a mission to send a request to your partners."
                      )}
                    />
                  }
                  renderRow={(m) => (
                    <Tr
                      key={m.id}
                      cursor="pointer"
                      onClick={() => handleMissionRowClick(m)}
                    >
                      <Td fontWeight="700">{m.title || "-"}</Td>
                      <Td color="portal.muted">{purposeLabel(m.purpose)}</Td>
                      <Td>
                        <StatusBadge
                          label={getStatusLabel(
                            t,
                            PARTNERS_MISSION_STATUS_LABEL_KEYS,
                            PARTNERS_MISSION_STATUS_LABELS,
                            m.status
                          )}
                          colorScheme={PARTNERS_MISSION_STATUS_COLORS[m.status] || "gray"}
                        />
                      </Td>
                      <Td textAlign="right">{m.recipientCount ?? 0}</Td>
                      <Td textAlign="right">{m.applicationCount ?? 0}</Td>
                      <Td>
                        {m.createdAt
                          ? new Date(m.createdAt).toLocaleDateString()
                          : "-"}
                      </Td>
                    </Tr>
                  )}
                />
              </Panel>
            </TabPanel>

            {/* ─── Consultants Tab ─── */}
            <TabPanel p={0}>
              <Panel
                title={t("partners.sentRequests.tabs.consultants", "Consultants")}
                count={t("partners.consultantProposals.summary.total", {
                  count: proposalsTotalItems,
                  defaultValue: "{{count}} proposal(s)",
                })}
                actions={
                  <Button
                    variant="primary"
                    size="sm"
                    h="28px"
                    px="10px"
                    fontSize="11px"
                    leftIcon={<FaUserPlus size="11px" />}
                    onClick={proposalCreateModal.onOpen}
                  >
                    {t(
                      "partners.consultantProposals.createButton",
                      "Propose Consultant"
                    )}
                  </Button>
                }
                filters={
                  <FilterBar>
                    <Box flex="1" minW="180px" />
                    <Select
                      value={proposalsStatusFilter}
                      onChange={(e) => {
                        setProposalsStatusFilter(e.target.value);
                        setProposalsPage(1);
                      }}
                      w={{ base: "100%", sm: "180px" }}
                      flex="0 0 auto"
                      placeholder={t(
                        "partners.sentRequests.consultantsAllStatuses",
                        "All statuses"
                      )}
                      sx={FILTER_SELECT_SX}
                    >
                      <option value="pending">{proposalStatusLabel("pending")}</option>
                      <option value="accepted">{proposalStatusLabel("accepted")}</option>
                      <option value="declined">{proposalStatusLabel("declined")}</option>
                    </Select>
                  </FilterBar>
                }
                footer={
                  <TablePagination
                    page={proposalsPage}
                    pageSize={proposalsPageSize}
                    totalItems={proposalsTotalItems}
                    totalPages={proposalsTotalPages}
                    onPageChange={setProposalsPage}
                    onPageSizeChange={(sz) => {
                      setProposalsPageSize(sz);
                      setProposalsPage(1);
                    }}
                  />
                }
              >
                <DataTable
                  columns={proposalColumns}
                  rows={proposals}
                  loading={proposalsLoading}
                  emptyState={
                    <EmptyState
                      icon={<FaUserTie />}
                      title={t(
                        "partners.consultantProposals.empty",
                        "No consultant proposals sent yet"
                      )}
                      hint={t(
                        "partners.consultantProposals.emptyHint",
                        "Propose a consultant to one of your partners to get started."
                      )}
                    />
                  }
                  renderRow={(p) => (
                    <Tr
                      key={p.id}
                      cursor="pointer"
                      onClick={() => {
                        setSelectedProposal(p);
                        proposalDetailModal.onOpen();
                      }}
                    >
                      <Td fontWeight="700">
                        {p.recipientCompanyName || `#${p.recipientCompanyId}`}
                      </Td>
                      <Td>{p.title || "—"}</Td>
                      <Td textAlign="right">
                        {p.dailyRate ? `${p.dailyRate} ${p.currency || "EUR"}` : "—"}
                      </Td>
                      <Td>
                        {p.availabilityDate
                          ? new Date(p.availabilityDate).toLocaleDateString()
                          : "—"}
                      </Td>
                      <Td>
                        <StatusBadge
                          label={proposalStatusLabel(p.status)}
                          colorScheme={PROPOSAL_STATUS_COLORS[p.status] || "gray"}
                        />
                      </Td>
                      <Td>
                        {p.createdAt
                          ? new Date(p.createdAt).toLocaleDateString()
                          : "-"}
                      </Td>
                    </Tr>
                  )}
                />
              </Panel>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Modals */}
      <MissionFormModal
        isOpen={missionCreateModal.isOpen}
        onClose={missionCreateModal.onClose}
        mission={null}
        onSuccess={() => fetchMissions()}
      />
      <MissionDetailModal
        isOpen={missionDetailModal.isOpen}
        onClose={() => {
          missionDetailModal.onClose();
          setSelectedMissionId(null);
        }}
        missionId={selectedMissionId}
        onRefresh={() => fetchMissions()}
      />
      <ConsultantProposalFormModal
        isOpen={proposalCreateModal.isOpen}
        onClose={proposalCreateModal.onClose}
        onSuccess={() => fetchProposals()}
      />
      <ConsultantProposalDetailModal
        isOpen={proposalDetailModal.isOpen}
        onClose={() => {
          proposalDetailModal.onClose();
          setSelectedProposal(null);
        }}
        proposal={selectedProposal}
        isReceiver={false}
      />
    </Box>
  );
};

export default PartnersMissions;
